import { autobind } from 'core-decorators';
import React, { Component } from 'react';
import { Layout } from 'app/blocks/blocks';
import { PrimaryButton, Button } from 'app/blocks/buttons/buttons';
import ASInfo from 'app/blocks/common/as-info';
import { ID, withCodes } from 'app/blocks/common/codes';
import { processing, tryCatch } from 'app/blocks/common/decorators';
import PageEnum from 'app/blocks/common/PageEnum';
import Alerts from 'app/blocks/common/spinner';
import * as Utils from 'app/blocks/common/utils';
import { refreshUser, getUser, logout } from 'app/blocks/middleware/authentication';
import * as middlewareRegister from 'app/blocks/middleware/register';
import { getUrlParam, invalidateUrlParam } from 'app/blocks/middleware/url-params';
import { MainTitle } from 'app/blocks/titles/titles';
import TOSConfirmations from 'app/pages/login/tos-confirmations';

const MENU_HIDER_CLASS = 'AS__hide_menu_and_footer';

type State = {
    isLoading: boolean;
    isSubmitting: boolean;

    terms: boolean;
    chinaPIPLConsent: boolean;
    chinaPIPLConsentError: string;
    wileyTermsOfUseConsent: boolean;
    wileyTermsOfUseConsentError: string;
    country?: { id: string; name: string };
    countryError?: string;
    areaOfInterest?: string;
    areaOfInterestError?: string;

    showCountry?: boolean;
    showConsents?: boolean;
    showAreas?: boolean;
    showPIPL?: boolean;
};

@autobind
class TermsOfServicePage extends Component<{ l: l }, State> {
    state: State = {
        isLoading: true,
        isSubmitting: false,

        terms: false,
        chinaPIPLConsent: false,
        chinaPIPLConsentError: null,
        wileyTermsOfUseConsent: false,
        wileyTermsOfUseConsentError: null,
        country: undefined,
        countryError: null,
        areaOfInterest: undefined,
        areaOfInterestError: null,

        showCountry: false,
        showConsents: false,
        showPIPL: false,
        showAreas: false,
    };

    @processing('isLoading')
    @tryCatch.popUpOnly
    async componentDidMount() {
        const { countryCode, countryNameNE, chinaPIPLConsent, sendEmailFlag } = await getUser();

        this.setState({
            showCountry: !countryCode,
            showConsents: true,
            showPIPL: !countryCode || (countryCode === 'CN' && !chinaPIPLConsent),
            showAreas: !sendEmailFlag,

            country: countryCode ? { id: countryCode, name: countryNameNE } : undefined,
        });

        document.body.classList.add(MENU_HIDER_CLASS);
    }

    componentWillUnmount() {
        document.body.classList.remove(MENU_HIDER_CLASS);
    }

    @processing('isSubmitting')
    @tryCatch.popUpOnly
    async onSubmit() {
        const { terms, country, areaOfInterest, wileyTermsOfUseConsent, chinaPIPLConsent } = this.state;

        if (!this.validation()) {
            return;
        }

        const req: Record<string, any> = {};

        if (this.state.showCountry) {
            req.countryCode = country?.id;
        }
        if (this.state.showAreas) {
            req.sendEmailFlag = terms;
            req.areaOfInterest = areaOfInterest || null;
        }
        if (this.state.showConsents) {
            req.wileyTermsOfUseConsent = wileyTermsOfUseConsent;
        }
        if (this.state.showPIPL && country?.id === 'CN') {
            req.chinaPIPLConsent = chinaPIPLConsent;
        }

        await middlewareRegister.registerExternalUser(req);
        await refreshUser();

        const returnUrl = getUrlParam('returnUrl');
        if (returnUrl) {
            Alerts.renderSpinner();
            invalidateUrlParam('returnUrl');
            Utils.windowLocationReplace(returnUrl);
        }
    }

    onTermsChange = checked =>
        this.setState({
            terms: checked,
            areaOfInterest: undefined,
            areaOfInterestError: null,
        });

    onChangeField = (key, value) =>
        this.setState(state => ({
            ...state,
            [key]: value,
            [`${key}Error`]: null,
        }));

    validation() {
        const {
            terms,
            country,
            areaOfInterest,
            wileyTermsOfUseConsent,
            chinaPIPLConsent,
            showCountry,
            showConsents,
            showPIPL,
        } = this.state;

        let ok = true;

        if (showCountry && !(country && country.id)) {
            ok = false;
            this.setState({
                countryError: 'REGISTRATION.errorMessages.REGISTRATION_COUNTRY_MISSING_ERR_TEXT',
            });
        }

        if (terms && !areaOfInterest) {
            ok = false;
            this.setState({
                areaOfInterestError: 'REGISTRATION.errorMessages.REGISTRATION_AREAS_MISSING_ERR_TEXT',
            });
        }

        if (showConsents && !wileyTermsOfUseConsent) {
            ok = false;
            this.setState({
                wileyTermsOfUseConsentError: 'REGISTRATION.errorMessages.TERMS_NOT_CONFIRMED',
            });
        }
        if (showPIPL && country?.id === 'CN' && !chinaPIPLConsent) {
            ok = false;
            this.setState({
                chinaPIPLConsentError: 'REGISTRATION.errorMessages.PIPL_NOT_CONFIRMED',
            });
        }

        return ok;
    }

    render() {
        const { l } = this.props;
        const {
            isLoading,
            isSubmitting,

            terms,
            wileyTermsOfUseConsent,
            chinaPIPLConsent,
            country,

            countryError,
            areaOfInterestError,
            wileyTermsOfUseConsentError,
            chinaPIPLConsentError,

            showCountry,
            showConsents,
            showPIPL,
            showAreas,
        } = this.state;

        if (isLoading) {
            return <Layout isLoading={isLoading} />;
        }

        return (
            <div className="RegisterPage container">
                <div className="Content">
                    <div className="Content-TermsOfService">
                        <MainTitle className="LogoAndTitle">
                            <a href="https://www.wiley.com/">
                                <img alt="" src="/asset/media/logo_black.svg" />
                            </a>
                            <br />
                            {l('REGISTRATION.TERMS_OF_SERVICE.TERMS_AND_POLICY_TITLE')}
                        </MainTitle>

                        <div className="RegisterBlock RegisterBlock-FormContainer">
                            <div
                                className="SubText"
                                dangerouslySetInnerHTML={{
                                    __html: l('REGISTRATION.uiLabelMessages.TERMS_UPDATED'),
                                }}
                            />

                            <TOSConfirmations
                                chinaPIPLConsent={chinaPIPLConsent}
                                country={country}
                                onChangeAreaOfInterest={selected => this.onChangeField('areaOfInterest', selected)}
                                onChangeChinaPIPLConsent={checked => this.onChangeField('chinaPIPLConsent', checked)}
                                onChangeCountry={pCountry => this.onChangeField('country', pCountry)}
                                onChangeSendEmail={this.onTermsChange}
                                onChangeWileyTermsOfUseConsent={c => this.onChangeField('wileyTermsOfUseConsent', c)}
                                onFocusAreas={() => this.setState({ areaOfInterestError: null })}
                                onFocusCountry={() => this.setState({ countryError: null })}
                                sendEmail={terms}
                                showAreas={showAreas}
                                showConsents={showConsents}
                                showCountry={showCountry}
                                showPIPL={showPIPL}
                                validationErrors={{
                                    country: countryError,
                                    areaOfInterest: areaOfInterestError,
                                    wileyTermsOfUseConsent: wileyTermsOfUseConsentError,
                                    chinaPIPLConsent: chinaPIPLConsentError,
                                }}
                                wileyTermsOfUseConsent={wileyTermsOfUseConsent}
                            />

                            <div className="RegisterRow RegisterRow-Actions TwoButtons">
                                <Button data-seleniumid="terms-logout-button" onClick={logout}>
                                    {l('REGISTRATION.TERMS_OF_SERVICE.LOGOUT')}
                                </Button>
                                <PrimaryButton
                                    className="action-anchor-terms-next"
                                    data-seleniumid="terms-submit-button"
                                    disabled={isSubmitting}
                                    onClick={this.onSubmit}
                                >
                                    {l(`REGISTRATION.TERMS_OF_SERVICE.${isSubmitting ? 'SUBMITTING' : 'SUBMIT'}`)}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
                <ASInfo page={PageEnum.TERMS_OF_SERVICE} />
            </div>
        );
    }
}

export { TermsOfServicePage };
export default withCodes(TermsOfServicePage, ID.REGISTRATION);
